import { CircularProgress, SxProps, Theme, FilterOptionsState, createFilterOptions } from "@mui/material";
import Autocomplete, {
  AutocompleteInputChangeReason,
  AutocompleteProps,
} from "@mui/material/Autocomplete";
import TextField, {
  TextFieldProps as StandardTextFieldProps,
} from "@mui/material/TextField";
import { alphabetical, get, omit, unique } from "radash";
import { KeyboardEvent, SyntheticEvent, useMemo, useState } from "react";
import { Control, FieldValues, useController } from "react-hook-form";

import {
  InputRegexFormatterTypes,
  useInputRegexFormatter,
} from "@hooks/useInputRegexFormatter";

import { convertToNumber } from "@utils/helpers";

type TextFieldType = Omit<StandardTextFieldProps, "onChange" | "onSelect">;
export type DynamicAutocompleteProps = TextFieldType &
  Omit<
    AutocompleteProps<OptionType, boolean, boolean, boolean>,
    "renderInput" | "onChange" | "onSelect"
  > & {
    name: string;
    valueKey?: string;
    control: Control<any>;
    skipSorting?: boolean;
    valueAsNumber?: boolean;
    disableMargins?: boolean;
    dynamicFreeForm?: boolean;
    allowCustom?: boolean;
    showErrorMessage?: boolean;
    formatterRules?: InputRegexFormatterTypes;
    onChange?: (value: any) => void;
    onSelect?: (option: OptionType) => void;
  };

type OptionType = FieldValues | string;

type HandleFreeFormInputChange = (
  event: React.SyntheticEvent | undefined,
  value: string,
  reason: AutocompleteInputChangeReason,
) => void;

export const LoadingEndAdornment = ({ loading }: { loading: boolean }) => {
  return loading ? (
    <CircularProgress
      size={18}
      sx={{
        position: "absolute",
        right: 10,
        top: 12,
        color: "grey",
      }}
    />
  ) : null;
};

const DynamicAutocomplete = ({
  sx,
  name,
  control,
  options,
  helperText,
  disableMargins,
  FormHelperTextProps,
  valueKey = "",
  loading = false,
  multiple = false,
  skipSorting = false,
  valueAsNumber = false,
  dynamicFreeForm = false,
  allowCustom = false,
  showErrorMessage = false,
  formatterRules = [],
  onChange,
  onSelect,
  ...rest
}: DynamicAutocompleteProps) => {
  const [inputValue, setInputValue] = useState("");
  const {
    field: { ref, onChange: handleFieldChange, ...fieldRest },
    fieldState,
  } = useController({
    control,
    name,
  });

  const inputFormatter = useInputRegexFormatter(formatterRules);

  const handleOnChange = (e: SyntheticEvent, selected: any) => {
    let newValue = null;

    if (multiple) {
      newValue = valueKey
        ? selected.map((option: object) =>
            inputFormatter(get(option, valueKey, option)),
          )
        : selected;
    } else {
      newValue = valueKey ? get(selected, valueKey, "") : selected;
    }

    if (valueAsNumber) {
      newValue = convertToNumber(newValue);
    }

    if (onChange) {
      onChange(newValue);
    } else {
      handleFieldChange(newValue);
    }

    onSelect && onSelect(selected);
  };

  const handleFreeFormInputChange: HandleFreeFormInputChange = (
    event,
    value,
  ) => {
    const prev = (fieldRest?.value as string[]) || [];

    const trimmed = inputFormatter(value.trim());
    if (trimmed && value.endsWith(" ")) {
      const newValue = valueAsNumber
        ? convertToNumber([...prev, trimmed])
        : [...prev, trimmed];

      const uniqueValues = unique(newValue);
      handleFieldChange(uniqueValues);
      setInputValue("");
    } else {
      setInputValue(trimmed || "");
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Backspace") {
      event.stopPropagation();
    }
  };

  const extractedValue = useMemo(() => {
    let value;
    if (typeof fieldRest.value === "string") {
      value = options.find(
        (option) => get(option, valueKey, option) === fieldRest.value,
      );
    } else if (multiple && !valueKey) {
      value = null;
    } else if (Array.isArray(fieldRest.value)) {
      value = fieldRest.value.map((value) => {
        const option = options.find(
          (option) => get(option, valueKey, null) === value,
        );
        return option;
      });
    }

    return value || fieldRest.value;
  }, [fieldRest.value, options, multiple, valueKey]);

  const normalizedOptions = useMemo(
    () =>
      skipSorting
        ? options
        : alphabetical(options, (item) => rest.getOptionLabel?.(item) || ""),
    [options],
  );

  const filter = createFilterOptions<OptionType>();
  const filterOptions = (options: OptionType[], params: FilterOptionsState<OptionType>) => {
    const filtered = filter(options, params);

    const { inputValue } = params;
    const isExisting = options.some((option) => inputValue === option);
    if (inputValue !== '' && !isExisting) {
      filtered.push(inputValue);
    }

    return filtered;
  }

  const errorMsg = fieldState.error?.message;
  const message = showErrorMessage ? errorMsg : helperText;
  const freeFormProps = dynamicFreeForm
    ? {
        inputValue,
        value: fieldRest.value,
        multiple: true,
        freeSolo: true,
        options: [],
        onInputChange: handleFreeFormInputChange,
        onBlur: (e: any) => {
          const value = e.target.value;
          if (value) {
            handleFreeFormInputChange(undefined, `${value} `, "input");
          }
        },
      }
    : {};

  return (
    <Autocomplete
      fullWidth
      limitTags={3}
      multiple={multiple}
      clearOnEscape={false}
      value={extractedValue}
      onChange={handleOnChange}
      options={normalizedOptions}
      disableCloseOnSelect={multiple}
      getOptionLabel={(option) => option?.toString()}
      sx={getStyles(sx, disableMargins)}
      slotProps={{ paper: { sx: { fontSize: 15 } } }}
      renderInput={(params: any) => (
        <TextField
          {...omit(params, ["key"])}
          {...fieldRest}
          fullWidth
          helperText={message}
          onKeyDown={handleKeyDown}
          label={rest.label as string}
          title={rest.label as string}
          error={!!fieldState.invalid}
          FormHelperTextProps={FormHelperTextProps}
          placeholder={rest.placeholder || (rest.label as string)}
          InputProps={{
            ...params.InputProps,
            ...(loading
              ? {
                  endAdornment: <LoadingEndAdornment loading={loading} />,
                }
              : {}),
          }}
        />
      )}
      {...(allowCustom 
          ? { filterOptions, freeSolo: true, clearOnBlur: true } 
          : {})}
      {...freeFormProps}
      {...rest}
    />
  );
};

const getStyles = (sx: SxProps<Theme> = {}, disableMargins?: boolean) => {
  let styles = {
    ...sx,
    "&.MuiAutocomplete-root .MuiInputBase-root": {
      backgroundColor: "white",
    },
  };

  return disableMargins
    ? { ...styles, "& .MuiFormControl-root": { my: 0 } }
    : { ...styles, maxHeight: 100 };
};

export default DynamicAutocomplete;
